"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { api } from "@repo/backend/convex/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const DISMISS_ERROR_MESSAGE = "We couldn't dismiss that update just yet. Please try again.";

export default function AnnouncementDialog() {
  const now = useMemo(() => Date.now(), []);
  const announcements = useQuery(api.announcements.listPendingForUser, { now });
  const acknowledge = useMutation(api.announcements.acknowledge);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const activeAnnouncement = useMemo(() => {
    if (!announcements || announcements.length === 0) {
      return undefined;
    }
    return announcements[Math.min(activeIndex, announcements.length - 1)];
  }, [announcements, activeIndex]);

  useEffect(() => {
    if (announcements === undefined) return; // loading state
    if (announcements.length === 0) {
      setOpen(false);
      setActiveIndex(0);
      return;
    }
    setActiveIndex(0);
    setOpen(true);
  }, [announcements]);

  const handleAcknowledge = useCallback(async () => {
    if (!activeAnnouncement) {
      setOpen(false);
      return;
    }
    if (isAcknowledging) return;
    setIsAcknowledging(true);
    try {
      await acknowledge({ announcementId: activeAnnouncement._id });
      if (announcements && activeIndex < announcements.length - 1) {
        setActiveIndex((prev) => prev + 1);
        setOpen(true);
      } else {
        setOpen(false);
      }
    } catch (error) {
      console.error("Failed to acknowledge announcement", error);
      toast.error(DISMISS_ERROR_MESSAGE);
      setOpen(true);
    } finally {
      setIsAcknowledging(false);
    }
  }, [acknowledge, activeAnnouncement, announcements, activeIndex, isAcknowledging]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!activeAnnouncement) {
        setOpen(nextOpen);
        return;
      }
      if (!nextOpen) {
        if (!activeAnnouncement.dismissible) {
          setOpen(true);
          return;
        }
        void handleAcknowledge();
        return;
      }
      setOpen(true);
    },
    [activeAnnouncement, handleAcknowledge],
  );

  const bodyParagraphs = useMemo(() => {
    if (!activeAnnouncement) return [] as string[];
    return activeAnnouncement.body
      .split(/\n\n+/)
      .map((paragraph) => paragraph.trim())
      .filter((paragraph) => paragraph.length > 0);
  }, [activeAnnouncement]);

  if (!activeAnnouncement) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" showCloseButton={activeAnnouncement.dismissible}>
        <DialogHeader>
          <DialogTitle>{activeAnnouncement.title}</DialogTitle>
          {bodyParagraphs.length > 0 && (
            <DialogDescription className="leading-relaxed">
              {bodyParagraphs[0]}
            </DialogDescription>
          )}
        </DialogHeader>
        {bodyParagraphs.slice(1).map((paragraph, index) => (
          <p key={index} className="text-sm leading-relaxed text-muted-foreground">
            {paragraph}
          </p>
        ))}
        <DialogFooter className="sm:flex-row sm:justify-between">
          {activeAnnouncement.ctaUrl && activeAnnouncement.ctaLabel && (
            <Button
              render={<Link href={activeAnnouncement.ctaUrl} />}
              variant="secondary"
              className="sm:mr-auto"
            >
              {activeAnnouncement.ctaLabel}
            </Button>
          )}
          <Button onClick={handleAcknowledge} disabled={isAcknowledging}>
            {isAcknowledging ? "Saving..." : "Got it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
